/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import React from 'react'
import {
  FacilityDetailItem,
  isFileItem,
  isRenderableFileItem,
} from '../../entites/FacilityDetailItem'
import { Icon } from '../../utils/Icon'
import { VStack } from './VStack'
import { Horizon } from '../map_facilities/Horizon'

export type ItemRowProps = {
  name: string
  href?: string
  note?: string
  as?: React.ElementType<any>
}

export const ItemRow = ({ name, href, note, as = 'li' }: ItemRowProps) => {
  return (
    <VStack css={itemRowStyle} as={as}>
      <Horizon columnGap="6px">
        <span className="check">✔</span>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {name}
            <Icon
              iconName="blank"
              style={{
                height: '14px',
                width: '14px',
                marginLeft: '4px',
              }}
            />
          </a>
        ) : (
          <span>{name}</span>
        )}
      </Horizon>
      {note && <p>{note}</p>}
    </VStack>
  )
}

const itemRowStyle = css`
  .check {
    font-weight: bold;
    font-size: 10px;
    line-height: 12px;
  }
  & a {
    text-decoration-line: underline;

    &:visited,
    &:link {
      color: inherit;
    }
  }

  & p {
    padding-left: 15px;
    font-size: 10px;
    line-height: 150%;
    color: #3f3d35;
  }
`

export type FileItemRowProps = {
  name: string
  url: string
  message?: string
  fileName?: string
  as?: React.ElementType<any>
}

/**
 * PDF 書類へのリンクの行。リスト項目（ItemRow）と同じ配列に混在して並ぶ。
 *
 * ✔ は付けず、メッセージ文のあとにファイルアイコン付きでリンクを出す。
 * テキスト未入力のときはアップロードしたファイル名で代替する
 */
export const FileItemRow = ({
  name,
  url,
  message,
  fileName,
  as: Component = 'li',
}: FileItemRowProps) => {
  return (
    <Component
      css={fileItemRowStyle}
      className={FILE_ITEM_ROW_CLASS}
      data-testid="usage-guide-file-row"
    >
      {message && <p className="message">{message}</p>}
      <div className="link">
        <Icon
          iconName="file_blank"
          style={{
            height: '14px',
            width: '14px',
          }}
        />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="usage-guide-file-link"
        >
          {fileLinkLabel(name, fileName)}
        </a>
      </div>
    </Component>
  )
}

// テキスト未入力ならアップロードしたファイル名で代替する。
// どちらも無い（Import や console 経由で作られた参照）ときは「PDF」を出す。
// 空文字のままにするとリンクが幅ゼロになり、画面上から存在が消える
const fileLinkLabel = (name: string, fileName?: string) =>
  name || fileName || 'PDF'

// 「持ちもの」のリストで、ファイル項目の前後だけ間隔を広げるためのフック。
// スタイルの当て先にテスト用の data-testid を使わないよう、クラスを別に持たせる
const FILE_ITEM_ROW_CLASS = 'usage-guide-file-row-spacing'

/**
 * 「持ちもの」のリスト（VStack gap="10px"）に当てる。
 *
 * **リスト行とファイル項目が隣り合うところだけ 25px 空ける**。
 * リスト行どうし・ファイル項目どうしは 10px のまま（grid では子の margin が
 * row-gap に加算されるので 10 + 15 = 25）。
 * 先頭のファイル項目には前の兄弟が無いため margin が付かず、見出しとの間隔は変わらない
 */
const usageItemListStyle = css`
  & > .${FILE_ITEM_ROW_CLASS} + :not(.${FILE_ITEM_ROW_CLASS}),
  & > :not(.${FILE_ITEM_ROW_CLASS}) + .${FILE_ITEM_ROW_CLASS} {
    margin-top: 15px;
  }
`

const fileItemRowStyle = css`
  display: grid;
  row-gap: 4px;

  & .message {
    font-size: 12px;
    line-height: 150%;
    color: #3f3d35;
  }

  & .link {
    display: grid;
    grid-auto-flow: column;
    column-gap: 6px;
    justify-content: left;
    align-items: center;
  }

  & a {
    text-decoration-line: underline;

    &:visited,
    &:link {
      color: inherit;
    }
  }
`

export type Item = FacilityDetailItem

/**
 * 「持ちもの」3 種に共通のリスト。
 * 間隔のルールをここ 1 箇所に閉じたいので、VStack ごとこのファイルで持つ
 */
export const UsageItemList = ({ items }: { items: Item[] }) => (
  <VStack gap="10px" as="ul" css={usageItemListStyle}>
    {items.map((item, i) => (
      <React.Fragment key={`${i}_${item.type ?? 'item'}_${item.name}`}>
        <UsageItemRow item={item} />
      </React.Fragment>
    ))}
  </VStack>
)

/**
 * 「持ちもの」3 種（登録時に必要なもの／施設に提出するもの／子どもが使うもの）に共通の 1 行。
 *
 * 3 つのカラムはサーバー側で同じ形として扱われるため、どのカラムにもファイル項目が入りうる。
 * ここで描き分けないと、ファイル項目が ✔ 付きのリスト行として出てしまい、
 * テキスト未入力の場合は幅ゼロの見えないリンクになる
 */
export const UsageItemRow = ({ item }: { item: Item }) => {
  if (isFileItem(item)) {
    // 未添付（url が空）のファイル項目は保存時に捨てられるため、Admin のプレビューでも出さない
    return isRenderableFileItem(item) ? (
      <FileItemRow
        name={item.name}
        // Admin プレビューは保存前でも開けるよう blob URL を優先する（保護者側では常に undefined）
        url={item.previewUrl || item.url}
        message={item.message}
        fileName={item.fileName}
      />
    ) : null
  }

  return <ItemRow name={item.name} href={item.url} note={item.note} />
}
